import { create } from 'zustand';
import { useAppStore } from './appStore';

// Helper to get user from localStorage
const getStoredUser = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

// Helper to get preferred timezone from localStorage or browser
const getStoredTimezone = () => {
  try {
    const stored = localStorage.getItem('timezone');
    if (stored) return stored;

    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) return detected;
    }
  } catch (e) {
    // Ignore and fall back to UTC
  }
  return 'UTC';
};

export const useAuthStore = create((set) => ({
  user: getStoredUser(),
  token: localStorage.getItem('token') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  tokenExpiresIn: localStorage.getItem('tokenExpiresIn') || null,
  timezone: getStoredTimezone(),
  permissions: null,
  
  setToken: (token, refreshToken = null, expiresIn = null) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    set({ token });
    
    // Store refresh token if provided
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
      set({ refreshToken });
    }
    
    // Store token expiration time if provided
    if (expiresIn) {
      localStorage.setItem('tokenExpiresIn', expiresIn);
      set({ tokenExpiresIn: expiresIn });
    }
  },
  
  setUser: (user) => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
    set({ user });
  },

  setPermissions: (permissions) => {
    // permissions is expected to be a map: { module: 'view' | 'edit' | 'full_access', ... }
    set({ permissions });
  },

  setTimezone: (timezone) => {
    if (timezone) {
      localStorage.setItem('timezone', timezone);
      set({ timezone });
    }
  },
  
  logout: () => {
    useAppStore.getState().resetAppStore();

    // Clear all authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresIn');
    localStorage.removeItem('user');
    sessionStorage.clear();
    
    // Clear any cached data
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    set({ token: null, refreshToken: null, tokenExpiresIn: null, user: null, permissions: null });
  },
  
  isLoggedIn: () => {
    const state = useAuthStore.getState();
    return state.token !== null;
  },
  
  getRefreshToken: () => {
    const state = useAuthStore.getState();
    return state.refreshToken;
  }
}));
